/**
 * NormalizingBackend — wraps a `Backend` and decodes wire
 * payloads to canonical form before cells surface to the sheet.
 *
 * Each source row's `RowMetadata.meta` bag carries the
 * server-declared `encoding` key (mirrors Rust
 * `SheetRowDb.metadata` JSONB). On every inbound cell path
 * (`fetchCells`, `onCellChange`), the adapter looks up the
 * encoding in the sheet's `EncodingRegistry` and decodes values
 * in place. Cells land in `SourceRowCellData`
 * already canonical; downstream consumers never see wire form.
 *
 * Every inbound path decodes by the one read rule
 * (`EncodingRegistry.decodeRead`, through `readOf`): a wire `null` stays a
 * null cell, a value the decoder rejects is left out before emitting, and
 * the read's rejections are counted against the row and traced once.
 *
 * Outbound paths (`writeCell`, `addRow`, `updateRow`, `deleteRow`)
 * pass through unchanged — writes leave the client in canonical
 * form; the upstream backend (if networked) handles
 * canonical→wire serialization on its side.
 */

import type { Option } from "../core/option.ts";
import type { SheetNetworkKey } from "../ids/sheet-network-key.ts";
import type { DateTime, NaiveDate, Utc } from "@tento-chrono";
import type { SheetsNetwork } from "../wire/rows-network.ts";

import type {
  Backend,
  BatchRowCells,
  CellChange,
  Etag,
  FetchedCells,
  RowChange,
  SurfaceKind,
  Unsubscribe,
} from "./rows-backend.ts";
import type { CellEntry, CellValue } from "../core/dtype.ts";
import type { Json } from "../core/json.ts";
import { EncodingRegistry } from "../core/encoding-registry.ts";
import type { RawCell, RawRow, RawSheet } from "../wire/raw-sheet-wire.ts";
import { RowId } from "../ids/row-id.ts";
import { CellId } from "../ids/ids.ts";
import { RowNetworkKey } from "../ids/row-network-key.ts";
import { SheetId } from "../ids/sheet-id.ts";

/**
 * Minimal lookup surface the adapter needs to resolve the
 * encoding for a given `RowId`. `Sheet` satisfies this shape
 * directly; tests can stub it.
 */
export interface EncodingLookup {
  readonly encodings: EncodingRegistry;
  encodingForRowId(rowId: RowId): Option<string>;
}

/**
 * A `Backend` wrapper that decodes each source row's wire cells to canonical values through its
 * declared encoding before the sheet sees them.
 */
export class NormalizingBackend implements Backend {
  readonly #inner: Backend;
  readonly #lookup: EncodingLookup;
  constructor(
    inner: Backend,
    lookup: EncodingLookup,
  ) {
    this.#lookup = lookup;
    this.#inner = inner;
  }

  async fetchSheet(sheetKey: SheetNetworkKey): Promise<Option<RawSheet>> {
    return this.#inner.fetchSheet(sheetKey);
  }

  async fetchCells(
    rowId: RowId,
    range: NaiveDate.Range,
  ): Promise<FetchedCells> {
    const raw = await this.#inner.fetchCells(rowId, range);
    const encoding = this.#lookup.encodingForRowId(rowId);
    if (encoding == null) return raw;
    const read = this.#lookup.encodings.readOf(encoding, NormalizingBackend.#backendKey(rowId));
    const cells = this.#decodeCells(read, raw.cells);
    const custom = this.#decodeCustom(read, raw.custom);
    read.end();
    return { cells, custom };
  }

  /**
   * Passes through in wire form: the encoding is declared per row of
   * this sheet (`encodingForRowId`), and a keyed read names no row.
   */
  fetchCellsByKey(key: RowNetworkKey, range: NaiveDate.Range): Promise<FetchedCells> {
    return this.#inner.fetchCellsByKey(key, range);
  }

  /** Passes through undecoded, as `fetchCellsByKey` does: the consumer decodes the wire cells once. */
  fetchSheetCells(
    sheetKey: SheetNetworkKey,
    range: NaiveDate.Range,
  ): Promise<SheetsNetwork.SheetRowDataResponse[]> {
    return this.#inner.fetchSheetCells(sheetKey, range);
  }

  async fetchCellsBatch(
    sheetKey: SheetNetworkKey,
    rowKeys: readonly RowNetworkKey[],
    range: NaiveDate.Range,
  ): Promise<Map<RowNetworkKey, BatchRowCells>> {
    const raw = await this.#inner.fetchCellsBatch(sheetKey, rowKeys, range);
    const out = new Map<RowNetworkKey, BatchRowCells>();
    for (const [key, result] of raw) {
      if (result.status !== "ok") {
        out.set(key, result);
        continue;
      }
      const encoding = this.#encodingForKey(key);
      if (encoding == null) {
        out.set(key, result);
        continue;
      }
      const read = this.#lookup.encodings.readOf(encoding, key.toString());
      const decoded = this.#decodeCells(read, result.cells);
      read.end();
      out.set(key, {
        status: "ok",
        rowKey: RowNetworkKey.fromWire(key),
        metadata: result.metadata,
        cells: decoded,
        syncKeys: result.syncKeys,
      });
    }
    return out;
  }

  onRowChange(handler: (change: RowChange) => void): Unsubscribe {
    return this.#inner.onRowChange((change) =>
      handler(this.#normalizeRowChange(change))
    );
  }

  onCellChange(
    rowId: RowId,
    handler: (change: CellChange) => void,
  ): Unsubscribe {
    return this.#inner.onCellChange(rowId, (change) => {
      const encoding = this.#lookup.encodingForRowId(rowId);
      if (encoding == null || change.kind !== "set") {
        handler(change);
        return;
      }
      const read = this.#lookup.encodings.readOf(encoding, NormalizingBackend.#backendKey(rowId));
      const decoded = read.decode(change.cell.value, change.cell.id ?? change.cell.date);
      read.end();
      if (decoded === EncodingRegistry.REJECTED) return;
      if (decoded == null || decoded === change.cell.value) {
        handler(change);
        return;
      }
      handler({
        kind: "set",
        cell: { ...change.cell, value: decoded },
        ...(change.etag !== undefined ? { etag: change.etag } : {}),
      });
    });
  }

  // ── Writes pass through ─────────────────────────────────────────────
  //
  // Outbound values are canonical by construction — the client
  // wrote them that way. The upstream backend owns any
  // canonical→wire serialization needed on its side.

  writeCell(
    rowId: RowId,
    cell: CellEntry,
    expectedEtag: Option<Etag>,
    requestId: Option<string>,
  ): Promise<void> {
    return this.#inner.writeCell(rowId, cell, expectedEtag, requestId);
  }

  clearCell(
    rowId: RowId,
    date: NaiveDate.Partial,
    id: Option<CellId>,
    expectedEtag: Option<Etag>,
  ): Promise<void> {
    return this.#inner.clearCell(rowId, date, id, expectedEtag);
  }

  writeTimepointCell(
    rowId: RowId,
    dateRange: DateTime.Range<Utc>,
    data: Option<Json | CellValue>,
    replaceCellId: Option<CellId>,
    expectedEtag: Option<Etag>,
  ): Promise<SheetsNetwork.CellWriteResponse> {
    return this.#inner.writeTimepointCell(
      rowId,
      dateRange,
      data,
      replaceCellId,
      expectedEtag,
    );
  }

  clearTimepointCell(
    rowId: RowId,
    cellId: CellId,
    expectedEtag: Option<Etag>,
  ): Promise<SheetsNetwork.CellWriteResponse> {
    return this.#inner.clearTimepointCell(rowId, cellId, expectedEtag);
  }

  addRow(
    row: RawRow,
    sheet: Option<SheetId>,
    position: Option<number>,
    expectedEtag: Option<Etag>,
    surface: Option<SurfaceKind>,
  ): Promise<Option<RowId>> {
    return this.#inner.addRow(row, sheet, position, expectedEtag, surface);
  }

  updateRow(
    rowId: RowId,
    patch: Backend.UpdateRowBody,
    expectedEtag: Option<Etag>,
  ): Promise<Backend.RowUpdated> {
    return this.#inner.updateRow(rowId, patch, expectedEtag);
  }

  deleteRow(rowId: RowId, expectedEtag: Option<Etag>): Promise<void> {
    return this.#inner.deleteRow(rowId, expectedEtag);
  }

  // ── Helpers ─────────────────────────────────────────────────────────

  #normalizeRowChange(change: RowChange): RowChange {
    if (change.kind === "add" && change.row.cells) {
      const rowCells = change.row.cells;
      const cells = this.#decodeRawCells(
        this.#encodingFromMeta(change.row.meta),
        change.row.id.toString(),
        rowCells,
      );
      if (cells === rowCells) return change;
      return { ...change, row: { ...change.row, cells } };
    }
    if (change.kind === "update" && change.patch.cells) {
      const patchCells = change.patch.cells;
      const cells = this.#decodeRawCells(
        this.#encodingFromMeta(change.patch.meta) ??
          this.#lookup.encodingForRowId(change.rowId),
        change.rowId.toString(),
        patchCells,
      );
      if (cells === patchCells) return change;
      return { ...change, patch: { ...change.patch, cells } };
    }
    return change;
  }

  /** Date-keyed `cells` under one read's rule: a rejected value is left out, a wire `null` kept as it came. */
  #decodeCells(read: EncodingRegistry.Read, cells: readonly CellEntry[]): CellEntry[] {
    const out: CellEntry[] = [];
    for (const cell of cells) {
      const value = read.decode(cell.value, cell.id ?? cell.date);
      if (value === EncodingRegistry.REJECTED) continue;
      out.push(value == null || value === cell.value ? cell : { ...cell, value });
    }
    return out;
  }

  /** Timepoint `entries` under one read's rule, as `#decodeCells`. */
  #decodeCustom(read: EncodingRegistry.Read, entries: readonly CellEntry.Timepoint[]): CellEntry.Timepoint[] {
    const out: CellEntry.Timepoint[] = [];
    for (const entry of entries) {
      const value = read.decode(entry.value, entry.id);
      if (value === EncodingRegistry.REJECTED) continue;
      out.push(value == null || value === entry.value ? entry : { ...entry, value });
    }
    return out;
  }

  #decodeRawCells(
    encoding: Option<string>,
    row: string,
    cells: readonly RawCell[],
  ): readonly RawCell[] {
    if (encoding == null) return cells;
    const read = this.#lookup.encodings.readOf(encoding, row);
    let changed = false;
    const out: RawCell[] = [];
    for (const cell of cells) {
      const decoded = read.decode(cell.value, cell.cellId ?? cell.date);
      if (decoded === EncodingRegistry.REJECTED) {
        changed = true;
        continue;
      }
      if (decoded === cell.value) {
        out.push(cell);
        continue;
      }
      changed = true;
      out.push({ ...cell, value: decoded });
    }
    read.end();
    return changed ? out : cells;
  }

  #encodingFromMeta(meta: RawRow["meta"]): Option<string> {
    const encoding = meta?.encoding;
    return typeof encoding === "string" ? encoding : null;
  }

  /**
   * The row's backend key, as a read route spells it and a batch answer keys
   * it (`RowNetworkKey.forRead`): the one row identity rejections count under
   * on every path (`EncodingRegistry.readOf`).
   */
  static #backendKey(rowId: RowId): string {
    return RowNetworkKey.forRead(rowId).toString();
  }

  #encodingForKey(rowKey: string): Option<string> {
    // The rowKey wire string parses directly into a RowId — RowId
    // accepts both UUID rows (`r_…`) and lookup-key rows (`:~…`).
    // The sheet's metadata store keys encoding off RowId, so no
    // separate lookup-key→encoding map is needed.
    let parsed: RowId;
    try {
      parsed = RowId.of(rowKey);
    } catch {
      return null;
    }
    return this.#lookup.encodingForRowId(parsed);
  }
}
